import React from 'react';
import { useModal, Body, Caption, styled, CustomLink, Tag, Shape, Button, Quantity, Flex, LazyImage } from '@teko/ds-desktop';
import { FreeShippingColored } from '@teko/ds-icon';
import { getComponentSize } from '../../helpers/index.es.js';
import { FlashSaleBar } from '../FlashSaleBar/index.es.js';
import { t } from '../../i18n.es.js';
import { setCookieOver18YearOld } from '@teko/utils';
import { useRouter } from 'next/router';
import { useQuantity } from '../../hooks/useQuantity.es.js';
import { FlashSaleBadge } from '../FlashSaleBadge/FlashSaleBadge.es.js';
import { SavingBadge } from '../SavingBadge/SavingBadge.es.js';
import { ProductPrice } from '../ProductPrice/ProductPrice.es.js';
import { EnhancedImage } from '../EnhancedImage/EnhancedImage.es.js';
import { useDevice } from '../../contexts/DeviceContext.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const SIZE = {
  s: {
    padding: "s12"
  },
  m: {
    padding: "s16"
  }
};
const ProductCard = (_a) => {
  var _b = _a, {
    name,
    link,
    imageUrl,
    price,
    freeship,
    hasGift,
    gifts = {},
    infoTags,
    titleTag,
    ctaButton,
    contactButton,
    stockQuantity,
    flashSale,
    addToCart,
    textColor,
    brand,
    tracking,
    ctaTracking,
    needConfirmAge,
    type,
    uom,
    precisionToDisplayDecimalQuantity: precision
  } = _b, props = __objRest(_b, [
    "name",
    "link",
    "imageUrl",
    "price",
    "freeship",
    "hasGift",
    "gifts",
    "infoTags",
    "titleTag",
    "ctaButton",
    "contactButton",
    "stockQuantity",
    "flashSale",
    "addToCart",
    "textColor",
    "brand",
    "tracking",
    "ctaTracking",
    "needConfirmAge",
    "type",
    "uom",
    "precisionToDisplayDecimalQuantity"
  ]);
  var _a2;
  const { isMobile } = useDevice();
  const { showModal } = useModal();
  const router = useRouter();
  const {
    quantity,
    setQuantity,
    handleUpdateQuantity,
    onIncrease,
    onDecrease
  } = useQuantity({
    addToCart,
    precision
  });
  const handleClickCtaButton = (e) => {
    var _a3;
    e.preventDefault();
    if (needConfirmAge) {
      showModal == null ? void 0 : showModal({
        title: isMobile ? t("C\u1EA3nh b\xE1o") : "",
        children: isMobile ? /* @__PURE__ */ React.createElement(Body, null, t("src.components.ProductCard.ProductCard.0")) : /* @__PURE__ */ React.createElement(ModalConfirmWrap, null, t("src.components.ProductCard.ProductCard.0")),
        okText: t("T\xF4i tr\xEAn 18 tu\u1ED5i"),
        cancelText: t("T\xF4i d\u01B0\u1EDBi 18 tu\u1ED5i"),
        onOk: () => {
          setCookieOver18YearOld(true);
          addToCart == null ? void 0 : addToCart.onAddToCart();
        },
        width: isMobile ? "90%" : "400px",
        okButtonProps: { block: isMobile, size: "s" },
        cancelButtonProps: { block: isMobile, size: "s" },
        alignFooter: "center",
        showCloseIcon: isMobile
      });
    } else
      (_a3 = ctaButton == null ? void 0 : ctaButton.onClick) == null ? void 0 : _a3.call(ctaButton, e);
  };
  const size = getComponentSize(isMobile);
  const renderButton = () => {
    const { quantityInCart = 0 } = addToCart || {};
    if (quantityInCart === 0) {
      if (ctaButton)
        return /* @__PURE__ */ React.createElement(Button, __spreadProps(__spreadValues(__spreadProps(__spreadValues({}, ctaButton), {
          onClick: (e) => {
            ctaButton.onClick && handleClickCtaButton(e);
          }
        }), ctaTracking), {
          size: "s",
          block: true,
          padding: "s0"
        }));
      else if (contactButton)
        return /* @__PURE__ */ React.createElement(Button, __spreadProps(__spreadValues(__spreadValues({}, contactButton), ctaTracking), {
          size: "s",
          block: true,
          padding: "s0"
        }));
    }
    if ((quantityInCart != null ? quantityInCart : 0) > 0) {
      return /* @__PURE__ */ React.createElement(Quantity, {
        block: true,
        quantity,
        setQuantity,
        onMinus: onDecrease,
        onPlus: onIncrease,
        min: 0,
        max: Math.min(stockQuantity, 999),
        color: "primary500",
        size: "s",
        onBlur: handleUpdateQuantity,
        onKeyPress: (e) => {
          if (e.key === "Enter")
            handleUpdateQuantity();
        },
        decimalSeparator: ".",
        precision
      });
    }
  };
  const productVisualWrapper = /* @__PURE__ */ React.createElement(ProductVisualWrapper, null, /* @__PURE__ */ React.createElement(ProductImageWrapper, null, /* @__PURE__ */ React.createElement(ProductImage, {
    hover: "zoom",
    height: "100%",
    objectFit: "contain",
    src: imageUrl,
    googleImageParams: {
      width: 230
    },
    aspectRatio: 1,
    alt: name
  })), ((_a2 = infoTags == null ? void 0 : infoTags.length) != null ? _a2 : 0) > 0 ? /* @__PURE__ */ React.createElement(InfoTagsWrapper, null, infoTags == null ? void 0 : infoTags.map((tag, index) => /* @__PURE__ */ React.createElement(InfoTag, __spreadValues({
    key: index
  }, tag)))) : null, flashSale ? /* @__PURE__ */ React.createElement(React.Fragment, null, flashSale.discountPercent && /* @__PURE__ */ React.createElement(FlashSaleBadgeWrapper, null, /* @__PURE__ */ React.createElement(FlashSaleBadge, {
    discountPercent: flashSale.discountPercent
  })), /* @__PURE__ */ React.createElement(FlashSaleTimerWrapper, null, /* @__PURE__ */ React.createElement(FlashSaleBar, __spreadProps(__spreadValues({}, flashSale), {
    endAt: typeof flashSale.endAt === "number" ? new Date(flashSale.endAt) : flashSale.endAt
  })))) : null, (price == null ? void 0 : price.discountAmount) ? /* @__PURE__ */ React.createElement(SavingBadgeWrapper, null, /* @__PURE__ */ React.createElement(SavingBadge, {
    savingAmount: price.discountAmount
  })) : null);
  const productName = /* @__PURE__ */ React.createElement(React.Fragment, null, brand && /* @__PURE__ */ React.createElement(Brand, null, /* @__PURE__ */ React.createElement(Body, {
    level: 2,
    line: 1,
    color: "textSecondary",
    hoverColor: "primary500",
    style: { textTransform: "uppercase", display: "inline" },
    onClick: (event) => {
      event.preventDefault();
      brand.code && router.push({
        pathname: "/brands/[brandId]",
        query: {
          brandId: brand.code
        }
      }, `/${brand.code}-brand.${brand.code}`);
    },
    className: "product-brand-name"
  }, brand.name)), /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ProductNameWrapper, null, /* @__PURE__ */ React.createElement(Caption, {
    className: "att-product-card-title",
    level: 1,
    line: 3,
    color: textColor || "textPrimary"
  }, titleTag && /* @__PURE__ */ React.createElement(TitleTag, __spreadValues({}, titleTag)), /* @__PURE__ */ React.createElement(StyledH3, {
    title: name
  }, name))), uom && /* @__PURE__ */ React.createElement(Caption, {
    level: 1,
    color: "textSecondary",
    style: { marginTop: "0.25rem" }
  }, t("\u0110\u01A1n v\u1ECB t\xEDnh"), ": ", uom), /* @__PURE__ */ React.createElement(ProductSaleInfo, {
    freeship: !!freeship
  }, freeship && /* @__PURE__ */ React.createElement(StyledIconWrapper, null, /* @__PURE__ */ React.createElement(FreeShippingColored, {
    size: 24
  })), !!price ? /* @__PURE__ */ React.createElement(ProductPrice, __spreadValues({}, price)) : null)));
  const productGift = () => {
    var _a3;
    if (!hasGift || !((_a3 = gifts == null ? void 0 : gifts.images) == null ? void 0 : _a3.length))
      return;
    const images = gifts.images.slice(0, 2);
    const more = gifts.images.length - 2;
    return /* @__PURE__ */ React.createElement(Flex, {
      align: "center",
      gap: "s4"
    }, images.map((image, index) => /* @__PURE__ */ React.createElement(StyledGift, {
      width: 24,
      height: 24,
      key: index
    }, /* @__PURE__ */ React.createElement(LazyImage, {
      src: image.src,
      height: "100%",
      width: "100%",
      borderRadius: 4,
      alt: image.alt,
      contain: true
    }))), more > 0 && /* @__PURE__ */ React.createElement(MoreGift, {
      width: 24,
      height: 24
    }, "+", more));
  };
  const card = type === "list" ? /* @__PURE__ */ React.createElement("div", __spreadValues({
    style: { display: "flex", padding: "12px" },
    className: "product-card"
  }, tracking), /* @__PURE__ */ React.createElement("div", {
    style: { flex: 3 }
  }, productVisualWrapper), /* @__PURE__ */ React.createElement("div", {
    style: { flex: 7, marginLeft: "12px" }
  }, productName, productGift(), renderButton())) : /* @__PURE__ */ React.createElement(Wrapper, __spreadValues({
    size,
    className: "product-card"
  }, tracking), /* @__PURE__ */ React.createElement(ProductLink, __spreadValues(__spreadValues({}, link), props), /* @__PURE__ */ React.createElement(ProductCardContent, null, productVisualWrapper, productName, productGift())), renderButton());
  return card;
};
const ProductLink = styled(CustomLink)`
  display: block;
`;
const Wrapper = styled.div(({ theme, size }) => `
    position: relative;
    width: 100%;
    height: 100%;
    padding: ${theme == null ? void 0 : theme.spacing[SIZE[size].padding]};
    display: flex;
    flex-direction: column;
    background-color: #fff;
    justify-content: space-between;
   `);
const StyledIconWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: auto;
`;
const ProductCardContent = styled.div(({ theme }) => `
    position: relative;
    flex: 1 0 auto;
    margin-bottom: ${theme == null ? void 0 : theme.spacing.s8};
`);
const ProductVisualWrapper = styled.div(({ theme }) => `
    position: relative;
    margin-bottom: ${theme == null ? void 0 : theme.spacing.s4};
`);
const ProductSaleInfo = styled.div(({ theme, freeship }) => `
    position: relative;
    margin-top: ${theme.spacing.s4};
    padding-right: ${freeship ? "1.5rem" : "unset"} ;
    margin-bottom: ${theme.spacing.s4}
`);
const Brand = styled.div(({ theme }) => `
    margin-bottom: ${theme == null ? void 0 : theme.spacing.s4};
`);
const TitleTag = styled(Tag)(({ theme }) => `
  margin-right: ${theme == null ? void 0 : theme.spacing.s4};
`);
const InfoTag = styled(Tag)(({ theme }) => `
    margin-bottom: ${theme == null ? void 0 : theme.spacing.s4};
  `);
const InfoTagsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: absolute;
  top: 0;
  left: 0;
`;
const FlashSaleBadgeWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;
const FlashSaleTimerWrapper = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
`;
const SavingBadgeWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
`;
const ProductImageWrapper = styled.div`
  position: relative;
  padding-bottom: 100%;
`;
const ProductImage = styled(EnhancedImage)`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  object-fit: contain;
`;
const ProductNameWrapper = styled.div`
  height: 3rem;
`;
const ModalConfirmWrap = styled.h5`
  margin-bottom: 0;
  color: ${(p) => p.theme.colors.textTitle};
  text-align: center;
`;
const StyledH3 = styled.h3`
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1rem;
  display: inline;
`;
const StyledGift = styled(Shape)`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.secondary50}; ;
`;
const MoreGift = styled(Shape)`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.secondary50};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
`;

export { ProductCard };
